import { useState } from 'react'
import { Link } from 'react-router-dom'
import type { Broadcast, BroadcastStatus, LiveConnection, LiveInput, LiveOverview, Show } from '../api/types'
import { useArmLive, useDisarmLive, useLive, useLiveAccess, useRevealedStreamKey, useRotateStreamKey } from '../hooks/useLive'
import { formatCredits, formatDuration, timeAgo } from '../utils/format'
import { OnAirBadge } from './LivePlayer'
import { Badge, Button, Card, CopyField, ErrorNote, Field, LoadingBlock, type Tone } from './ui'

const CONNECTION: Record<LiveConnection, { tone: Tone; label: string }> = {
  idle: { tone: 'neutral', label: 'Waiting for OBS' },
  connected: { tone: 'ok', label: 'OBS connected' },
  reconnecting: { tone: 'warn', label: 'OBS reconnecting' },
  errored: { tone: 'danger', label: 'Connection failed' },
}

const BROADCAST: Record<BroadcastStatus, { tone: Tone; label: string }> = {
  live: { tone: 'danger', label: 'On air' },
  processing: { tone: 'warn', label: 'Finishing the recording' },
  downloading: { tone: 'warn', label: 'Preparing the file' },
  imported: { tone: 'ok', label: 'Episode made' },
  failed: { tone: 'danger', label: 'Failed' },
}

/** Going live from OBS: arm the show, what OBS needs, and what became of each broadcast. */
export function LiveCard({ workspaceId, show, canEdit }: { workspaceId: string; show: Show; canEdit: boolean }) {
  const live = useLive(workspaceId, show.id)
  return (
    <Card title="Live" actions={live.data?.input?.on_air ? <OnAirBadge /> : undefined}>
      {live.isPending && <LoadingBlock />}
      {live.isError && <ErrorNote error={live.error} />}
      {live.data && <LiveBody workspaceId={workspaceId} showId={show.id} live={live.data} canEdit={canEdit} />}
    </Card>
  )
}

function LiveBody({ workspaceId, showId, live, canEdit }: { workspaceId: string; showId: string; live: LiveOverview; canEdit: boolean }) {
  if (!live.available) return <p className="muted">Live streaming is not set up on this server yet.</p>
  if (!live.included) {
    return (
      <div className="stack-sm">
        <p className="muted">
          Stream from OBS to a public live page. Every broadcast is recorded and becomes an episode you can transcribe like an upload.
          This workspace's plan doesn't include it.
        </p>
        <div>
          <Link className="btn btn-secondary" to="/studio/billing">See plans</Link>
        </div>
      </div>
    )
  }
  const input = live.input
  return (
    <div className="stack">
      {!canEdit ? (
        <p className="muted">{input?.on_air ? 'This show is on air.' : 'Editors can take this show live from OBS.'}</p>
      ) : input?.on_air ? (
        <OnAir workspaceId={workspaceId} showId={showId} input={input} pageUrl={live.page_url} />
      ) : input?.armed ? (
        <WaitingForObs workspaceId={workspaceId} showId={showId} input={input} />
      ) : (
        <GoLive workspaceId={workspaceId} showId={showId} error={input?.error ?? null} />
      )}
      <details className="live-more">
        <summary>More live settings</summary>
        <div className="stack">
          {canEdit && input && <NewKey workspaceId={workspaceId} showId={showId} />}
          <AccessControl workspaceId={workspaceId} showId={showId} live={live} canEdit={canEdit} />
          <p className="muted small">
            {live.price_per_minute > 0 || live.price_per_viewer_minute > 0
              ? `${formatCredits(live.price_per_minute, 'up')} credits a minute on air and ${formatCredits(live.price_per_viewer_minute, 'up')} a minute watched, counting every viewer.`
              : 'Going live is free: neither the minutes you record nor the minutes people watch cost credits.'}{' '}
            Your plan ends a broadcast that runs past its limit.{' '}
            <a href={live.page_url} target="_blank" rel="noopener noreferrer">Public live page ↗</a>
          </p>
        </div>
      </details>
      {live.broadcasts.length > 0 && <Broadcasts broadcasts={live.broadcasts} />}
    </div>
  )
}

/** Step 0: one button. The title is optional. */
function GoLive({ workspaceId, showId, error }: { workspaceId: string; showId: string; error: string | null }) {
  const [title, setTitle] = useState('')
  const arm = useArmLive(workspaceId, showId)
  return (
    <form
      className="go-live"
      onSubmit={(event) => {
        event.preventDefault()
        arm.mutate({ title: title.trim() }, { onSuccess: () => setTitle('') })
      }}
    >
      <div className="stack-sm">
        <h3>Go live from OBS</h3>
        <p className="muted small">You'll get a server and a stream key to paste into OBS. The recording becomes a draft episode when you stop.</p>
      </div>
      <div className="go-live-row">
        <input
          id="broadcast-title"
          aria-label="Broadcast title (optional)"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          maxLength={300}
          placeholder="Title (optional)"
        />
        <Button type="submit" busy={arm.isPending}>Go live</Button>
      </div>
      {error && <ErrorNote message={error} />}
      {arm.error && <ErrorNote error={arm.error} />}
    </form>
  )
}

/** Step 1: the server and key are shown straight away, then OBS's own button. */
function WaitingForObs({ workspaceId, showId, input }: { workspaceId: string; showId: string; input: LiveInput }) {
  const streamKey = useRevealedStreamKey(workspaceId, showId)
  const disarm = useDisarmLive(workspaceId, showId)
  const key = streamKey.data
  const connection = CONNECTION[input.connection]
  return (
    <div className="stack">
      <ol className="obs-steps">
        <li>
          <strong>In OBS, open Settings → Stream</strong> and choose Service: <em>Custom…</em>
        </li>
        <li>
          <strong>Paste these two:</strong>
          <div className="live-grid">
            <CopyField label="Server" value={input.rtmps_url} />
            {key ? (
              <CopyField label="Stream key" value={key.stream_key} />
            ) : (
              <div className="copy-field">
                <span className="setting-label">Stream key</span>
                <div className="row">
                  <code>{streamKey.isPending ? 'Getting your key…' : input.stream_key_hint}</code>
                  {streamKey.isError && <Button variant="secondary" onClick={() => void streamKey.refetch()}>Try again</Button>}
                </div>
              </div>
            )}
          </div>
          <span className="muted small">Anyone with the key can broadcast as this show, so keep it off screen.</span>
        </li>
        <li>
          <strong>Press Start Streaming in OBS.</strong> This page notices by itself. Stopping in OBS ends the recording; the show stays ready for
          ten minutes in case you start again (OBS's bandwidth test counts as a start).
        </li>
      </ol>
      <div className="row between">
        <span className="row">
          <Badge tone={connection.tone}>{connection.label}</Badge>
          {input.title && <span className="muted small">“{input.title}”</span>}
        </span>
        <Button variant="ghost" busy={disarm.isPending} onClick={() => disarm.mutate(undefined)}>Cancel</Button>
      </div>
      {input.error && <ErrorNote message={input.error} />}
      {streamKey.error && <ErrorNote error={streamKey.error} />}
      {disarm.error && <ErrorNote error={disarm.error} />}
    </div>
  )
}

/** Step 2: on air. Stopping here ends it (stopping in OBS does too). */
function OnAir({ workspaceId, showId, input, pageUrl }: { workspaceId: string; showId: string; input: LiveInput; pageUrl: string }) {
  const disarm = useDisarmLive(workspaceId, showId)
  return (
    <div className="stack-sm">
      <div className="row between">
        <span className="row">
          <OnAirBadge />
          <strong>You're live{input.title ? `: ${input.title}` : ''}</strong>
          {input.connection === 'reconnecting' && <Badge tone="warn">OBS reconnecting</Badge>}
        </span>
        <span className="row">
          <a className="btn btn-secondary" href={pageUrl} target="_blank" rel="noopener noreferrer">Watch ↗</a>
          <Button variant="danger" busy={disarm.isPending} onClick={() => disarm.mutate(undefined)}>End broadcast</Button>
        </span>
      </div>
      {input.error && <ErrorNote message={input.error} />}
      {disarm.error && <ErrorNote error={disarm.error} />}
    </div>
  )
}

/** A new stream key, when the old one leaked. It replaces the key shown in the OBS steps. */
function NewKey({ workspaceId, showId }: { workspaceId: string; showId: string }) {
  const streamKey = useRotateStreamKey(workspaceId, showId)
  return (
    <div className="row">
      <Button
        variant="ghost"
        busy={streamKey.isPending}
        onClick={() => {
          if (window.confirm('Make a new stream key? The old one stops working and OBS will need the new one.')) streamKey.mutate()
        }}
      >
        Make a new stream key
      </Button>
      {streamKey.data && <span className="muted small">Done. Paste the new key into OBS; it shows in the steps when you go live.</span>}
      {streamKey.error && <ErrorNote error={streamKey.error} />}
    </div>
  )
}

function Broadcasts({ broadcasts }: { broadcasts: Broadcast[] }) {
  return (
    <div className="table-wrap">
      <table className="table">
        <thead>
          <tr><th>Broadcast</th><th>Status</th><th>Length</th><th>Viewer time</th><th>Episode</th></tr>
        </thead>
        <tbody>
          {broadcasts.map((broadcast) => {
            const status = BROADCAST[broadcast.status]
            return (
              <tr key={broadcast.id}>
                <td>
                  <div>{broadcast.title || 'Untitled broadcast'}</div>
                  <div className="muted small">{timeAgo(broadcast.started_at)}</div>
                </td>
                <td>
                  <Badge tone={status.tone}>{status.label}</Badge>
                  {broadcast.error && <div className="muted small">{broadcast.error}</div>}
                </td>
                <td className="muted">{formatDuration(broadcast.duration_ms)}</td>
                <td className="muted" title={
                    broadcast.metered_at
                      ? `Everything viewers watched, added up. Read from Cloudflare Stream ${timeAgo(broadcast.metered_at)}.`
                      : 'Not read from Cloudflare Stream yet.'
                  }>
                  {broadcast.metered_at ? formatDuration(broadcast.watched_ms) : '—'}
                </td>
                <td>{broadcast.episode_id ? <Link to={`/studio/episodes/${broadcast.episode_id}`}>{broadcast.episode_title ?? 'Open'}</Link> : <span className="muted">—</span>}</td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

/** Who can watch: anyone on the public live page, or only on the show's own site. */
function AccessControl({ workspaceId, showId, live, canEdit }: { workspaceId: string; showId: string; live: LiveOverview; canEdit: boolean }) {
  const change = useLiveAccess(workspaceId, showId)
  const onAir = Boolean(live.input?.on_air)
  return (
    <div className="stack-sm">
      <Field
        label="Who can watch"
        hint={
          live.access === 'restricted'
            ? 'Only through your own site: its server asks for a short-lived link (POST …/live/playback) once it has decided a viewer may watch. The public live page says it is on air but never plays it.'
            : 'Anyone, on the public live page and wherever the stream is embedded.'
        }
      >
        <select
          value={live.access}
          disabled={!canEdit || onAir || change.isPending}
          onChange={(e) => change.mutate(e.target.value as LiveOverview['access'])}
        >
          <option value="public">Anyone</option>
          <option value="restricted">Only through my site (members only)</option>
        </select>
      </Field>
      {onAir && <p className="muted small">This can change between broadcasts, not while on air.</p>}
      {change.error && <ErrorNote error={change.error} />}
    </div>
  )
}
